// SwedishTimeCounter.jsx
import React from 'react';
import { motion, AnimatePresence } from "framer-motion";
import timeData from './data/landsdelar.json';
import { logger } from './config/logging';

// Funktion för att beräkna tidsdifferensen mellan start och slut
function calculateTimeDiff(start, end) {
  const ms = end - start;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours   = Math.floor(minutes / 60);
  const days    = Math.floor(hours / 24);
  const months  = Math.floor(days / 30.44); // ungefärligt
  const years   = Math.floor(months / 12);
  return {
    years,
    months: months % 12,
    days: days % 30,
    hours: hours % 24,
    minutes: minutes % 60,
    seconds: seconds % 60,
  };
}

export default function SwedishTimeCounter({ currentDate, selectedRegionId, onRegionSelect }) {
  const componentName = 'SwedishTimeCounter';
  logger.debug(componentName, 'Komponenten renderas', { selectedRegionId, hasOnRegionSelect: !!onRegionSelect });
  
  const resultMap = {};

  // Gruppera regioner efter landsdel och samla alla ID:n
  for (const row of timeData) {
    if (row.hidden) continue;

    const key = row.landsdel;
    if (!resultMap[key]) {
      resultMap[key] = { 
        ms: 0, 
        name: key, 
        isSverige: row.id === "se-fi", // Fixa jämförelse
        regionIds: [], // Samla alla ID:n för denna landsdel
        primaryRegionId: row.id // Huvud-ID för denna landsdel
      };
    }

    // Lägg till ID:t till listan
    resultMap[key].regionIds.push(row.id);

    const start = new Date(row.startdatum);
    const end = row.slutdatum === null ? currentDate : new Date(row.slutdatum);

    if (currentDate > start) {
      const periodEnd = currentDate < end ? currentDate : end;
      resultMap[key].ms += periodEnd - start;
    }
  }

  logger.debug(componentName, 'Resultatkarta', resultMap);

  const sortedRendered = Object.values(resultMap)
    .sort((a, b) => {
      if (a.isSverige) return -1;
      if (b.isSverige) return 1;
      return b.ms - a.ms;
    })
    .map(({ name, ms, regionIds, primaryRegionId }) => {
      const time = calculateTimeDiff(0, ms);
      // En räknare är markerad om något av dess region-ID:n matchar den valda regionen
      const isSelected = regionIds.includes(selectedRegionId);
      
      logger.debug(componentName, `Räknare ${name}`, { regionIds, primaryRegionId, isSelected, selectedRegionId });
      
      // Funktion för att hantera klick på räknaren
      const handleCounterClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        logger.debug(componentName, `Klickade på räknare: ${name}`, { regionIds, primaryRegionId, isSelected });
        
        if (onRegionSelect) {
          // Om räknaren redan är markerad, avmarkera den
          if (isSelected) {
            logger.debug(componentName, 'Avmarkerar räknare');
            onRegionSelect(null);
          } else {
            // Annars, markera den primära regionen för denna landsdel
            logger.debug(componentName, 'Markerar räknare med ID:', primaryRegionId);
            onRegionSelect(primaryRegionId);
          }
        } else {
          logger.warn(componentName, 'onRegionSelect är inte definierad!');
        }
      };
      
      return (
        <motion.div
          key={name}
          layout
          layoutId={name}
          transition={{ duration: 0.4 }}
          onClick={handleCounterClick}
          onMouseDown={(e) => e.preventDefault()} // Förhindra text-selection
          className={`p-3 border-2 rounded-lg shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md select-none ${
            isSelected 
              ? 'border-black bg-blue-50 ring-2 ring-blue-200' 
              : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
          }`}
        >
          <strong className={isSelected ? 'text-blue-800' : 'text-gray-800'}>
            {name}:
          </strong>{" "}
          <AnimatedTime time={time} isSelected={isSelected} />
        </motion.div>
      );
    });

  return (
    <section className="rounded-2xl bg-white shadow-md p-4 mt-4">
      <h2 className="text-lg font-semibold mb-2">Tid som landsdelar tillhört Sverige:</h2>
      <motion.div layout className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <AnimatePresence mode="popLayout">
          {sortedRendered}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}

// Komponent som animerar varje tidsdel
function AnimatedTime({ time, isSelected }) {
  const units = [" år ", " mån ", " d  ", " h ", " min ", " s"];

  return (
    <span className="flex space-x-4 text-sm font-mono tabular-nums">
      {["years", "months", "days", "hours", "minutes", "seconds"].map((unit, i) => (
        <span key={`${unit}-${time[unit]}`} className="flex items-baseline">
          <motion.span
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.1 }}
            className={`inline-block w-[4ch] text-right ${
              isSelected ? 'text-blue-700' : 'text-gray-700'
            }`}
          >
            {time[unit]}
          </motion.span>
          <span className={`ml-1 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`}>
            {units[i]}
          </span>
        </span>
      ))}
    </span>
  );
}